export class ApiError extends Error {
    public readonly statusCode: number;
    public readonly errorCode?: string;
    public readonly metadata?: Record<string, any>;

    constructor(
        message: string,
        statusCode: number,
        errorCode?: string,
        metadata?: Record<string, any>
    ) {
        super(message);
        this.name = "ApiError";
        this.statusCode = statusCode;
        this.errorCode = errorCode;
        this.metadata = metadata;
    }
}

export const POST = async (url: string, body: any) => {
    const response = await fetch(url, {
        method: "POST",
        body: JSON.stringify(body),
        headers: { "Content-Type": "application/json" },
        credentials: "include" // Ensure cookies are included in the request
    });

    if (!response.ok) {
        // Try to parse error details from response body
        try {
            const errorData = await response.json();
            throw new ApiError(
                errorData.message || response.statusText || `Failed to POST to ${url}`,
                response.status,
                errorData.error,
                errorData.metadata
            );
        } catch (parseError) {
            // If JSON parsing fails, throw basic error
            if (parseError instanceof ApiError) {
                throw parseError;
            }
            throw new ApiError(response.statusText || `Failed to POST to ${url}`, response.status);
        }
    }

    return response.json();
};

export const POSTForm = async (url: string, data: Record<string, any>) => {
    // Use URLSearchParams for URL-encoded form data (application/x-www-form-urlencoded)
    // This is more compatible with Express body-parser than multipart FormData
    const params = new URLSearchParams();

    Object.entries(data).forEach(([key, value]) => {
        params.append(key, String(value));
    });

    const response = await fetch(url, {
        method: "POST",
        body: params,
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        credentials: "include" // Ensure cookies are included in the request
    });

    if (!response.ok) {
        throw new Error(response.statusText || `Failed to POST to ${url}`);
    }

    return response.json();
};

export const GET = async (url: string) => {
    const response = await fetch(url, {
        credentials: "include" // Ensure cookies are included in the request
    });

    if (!response.ok) {
        throw new Error(response.statusText || `Failed to GET from ${url}`);
    }

    return response.json();
};

export const GETBlob = async (url: string): Promise<Blob> => {
    const response = await fetch(url, {
        credentials: "include" // Ensure cookies are included in the request
    });

    if (!response.ok) {
        throw new Error(response.statusText || `Failed to GET Blob from ${url}`);
    }

    return response.blob();
};

export const POSTBlob = async (url: string, body: any): Promise<Blob> => {
    const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
        credentials: "include" // Ensure cookies are included in the request
    });

    if (!response.ok) {
        throw new Error(response.statusText || `Failed to POST Blob to ${url}`);
    }

    return response.blob();
};

export const DELETE = async (url: string, body: any) => {
    const response = await fetch(url, {
        method: "DELETE",
        body: JSON.stringify(body),
        headers: { "Content-Type": "application/json" },
        credentials: "include" // Ensure cookies are included in the request
    });

    if (!response.ok) {
        throw new Error(response.statusText || `Failed to DELETE from ${url}`);
    }

    return response.json();
};
